import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitTriviaAnswer } from '../../store/thunks/triviaThunks';

const DailyTrivia = () => {
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const dispatch = useDispatch();

    const { entries } = useSelector(state => state.entries);
    const { dailyTrivia, loading: isLoading, error, submissionResult, lastSubmission } = useSelector(state => state.trivia);

    // Reset state when trivia question changes
    useEffect(() => {
        setSelectedAnswer(null);
    }, [dailyTrivia?._id]);

    // Check if user has already answered today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hasAnsweredForToday = entries?.some(entry => {
        const entryDate = new Date(entry.timestamp);
        entryDate.setHours(0, 0, 0, 0);
        return entry.entryType === 'daily_trivia' && entryDate.getTime() === today.getTime();
    });

    const handleAnswerSelect = async (answer) => {
        setSelectedAnswer(answer);
        try {
            await dispatch(submitTriviaAnswer({
                triviaId: dailyTrivia._id,
                answer
            })).unwrap();
        } catch (err) {
            // Reset selected answer if submission fails so user can try again
            setSelectedAnswer(null);
        }
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>;
    }

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-center mb-6">Daily Trivia Challenge</h2>
            <h2 className="text-2xl font-bold text-center mb-6">Earn 3 entries for a correct guess today!</h2>
            
            <p className="text-gray-700 mb-6 text-3xl font-semibold text-center px-4">
                {!dailyTrivia?.question ? 'No trivia question available today' : dailyTrivia.question}
            </p>

            <div className="flex flex-col space-y-4">
                {hasAnsweredForToday ? (
                    <div className="text-center p-4">
                        You've already submitted your answer for today. Come back tomorrow!
                    </div>
                ) : (
                    dailyTrivia?.options && Object.entries(dailyTrivia.options).map(([key, value]) => (
                        <button
                            key={key}
                            onClick={() => handleAnswerSelect(value)}
                            className={`w-full p-4 text-left rounded-lg transition-colors duration-200 
                                ${selectedAnswer === value 
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }
                                ${submissionResult !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={submissionResult !== null}
                        >
                            {value}
                        </button>
                    ))
                )}
            </div>

            {isLoading && (
                <div className="mt-6 p-4 rounded-lg text-center text-white bg-blue-500">
                    Submitting your answer...
                </div>
            )}

            {lastSubmission && (
                <div className={`mt-6 p-4 rounded-lg text-center text-white ${lastSubmission.isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                    {lastSubmission.isCorrect 
                        ? "Correct! You've earned 3 contest entries. Come back tomorrow for another question!"
                        : "Not quite right, but you still earned 1 contest entry for trying. Come back tomorrow for another question!"}
                </div>
            )}

            {error && (
                <div className="mt-6 p-4 rounded-lg text-center text-white bg-red-500">
                    There was an error submitting your answer. Please try again.
                </div>
            )}
        </div>
    );
};

export default DailyTrivia;